import Column from '@app/components/layout/Column'
import { Form } from '@app/components/core/form/Form'
import { RegionSelectorField } from '@app/components/core/form/fields/RegionSelectorField'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useMemo } from 'react'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { Initiative } from '@schema/initiative'
import { FieldEditActions } from '../shared/FieldEditActions'
import { useEditContainerClose } from '../shared/useEditContainerClose'
import type { UseFormReturn } from 'react-hook-form'
import {
  createMembersParametersRegionSchema,
  type MembersParametersRegionFormValues,
} from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'

export type RegionFieldEditProps = {
  initiativeId: number
  initiative: Partial<Initiative>
}

const RegionFormBody = ({
  methods,
  regionId,
  regionName,
  onCancel,
}: {
  methods: UseFormReturn<MembersParametersRegionFormValues>
  regionId: number
  regionName: string
  onCancel: () => void
}) => {
  const { reset, formState, trigger } = methods

  useEffect(() => {
    reset({
      region: {
        key: regionId,
        value: regionName,
      },
    })
    void trigger()
  }, [regionId, regionName, reset, trigger])

  return (
    <Column gap="1rem" alignItems="stretch">
      <RegionSelectorField<MembersParametersRegionFormValues>
        name="region"
      />
      <FieldEditActions
        onCancel={onCancel}
        saveDisabled={!formState.isValid || formState.isSubmitting}
      />
    </Column>
  )
}

export const RegionFieldEdit = ({
  initiative,
  initiativeId,
}: RegionFieldEditProps) => {
  const close = useEditContainerClose()
  if (!close) {
    throw new Error('RegionFieldEdit requires an EditContainer ancestor')
  }

  const client = useClient()
  const { showFailToast, showSuccessToast } = useToast()
  const t = useTranslations().app.common
  const formValidation = useTranslations().app.form.validation

  const regionId = initiative.location?.address?.region?.id ?? 0
  const regionName = initiative.location?.address?.region?.name ?? ''
  const localityId = initiative.location?.address?.locality?.id

  const schema = useMemo(
    () => createMembersParametersRegionSchema(formValidation.required),
    [formValidation.required],
  )

  const onSubmit = useCallback(
    (data: MembersParametersRegionFormValues) => {
      void client
        .call('edit-initiative', {
          id: initiativeId,
          value: {
            location: {
              address: {
                region: { id: data.region.key },
                locality: localityId ? { id: localityId } : undefined,
                country: undefined,
                municipality: undefined,
                street: null,
                postalCode: null,
              },
              geo: undefined,
            },
          },
        })
        .then((success) => {
          if (success) {
            showSuccessToast(t.toastSuccess)
            close()
          } else {
            showFailToast()
          }
        })
        .catch(() => showFailToast())
    },
    [
      client,
      initiativeId,
      localityId,
      close,
      showFailToast,
      showSuccessToast,
      t.toastSuccess,
    ],
  )

  return (
    <Form<MembersParametersRegionFormValues>
      schema={schema}
      defaultValues={{
        region: {
          key: regionId,
          value: regionName,
        },
      }}
      mode="onChange"
      onSubmit={onSubmit}
    >
      {(methods) => (
        <RegionFormBody
          methods={methods}
          regionId={regionId}
          regionName={regionName}
          onCancel={() => {
            methods.reset({
              region: {
                key: regionId,
                value: regionName,
              },
            })
            close()
          }}
        />
      )}
    </Form>
  )
}
